// database/economyStore.js
// Basado en ENGINE_GACHA_ECONOMY_STORAGE_SPEC_v1

const DB_NAME = "GachaHospitalDB";
const DB_VERSION = 3; // Incremented for new indexes
const STORE_NAME = "economyStore";
const ROSTER_STORE = "rosterStore";
const LOG_STORE = "logStore";
const LEARNING_STORE = "learningStore";

/**
 * Initializes IndexedDB with proper version migration and error handling.
 */
export async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      const oldVersion = e.oldVersion;
      console.log(`[DB] Upgrading from ${oldVersion} to ${DB_VERSION}`);

      try {
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
        }
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains(ROSTER_STORE)) db.createObjectStore(ROSTER_STORE);
          if (!db.objectStoreNames.contains(LOG_STORE)) db.createObjectStore(LOG_STORE);
          if (!db.objectStoreNames.contains(LEARNING_STORE)) db.createObjectStore(LEARNING_STORE);
        }
        if (oldVersion < 3) {
            // Add indexes for efficient cleanup/querying [cite: 4, 11]
            const logStore = db.objectStoreNames.contains(LOG_STORE) ? e.target.transaction.objectStore(LOG_STORE) : db.createObjectStore(LOG_STORE);
            if (!logStore.indexNames.contains('timestamp')) logStore.createIndex('timestamp', 'timestamp', { unique: false });
            
            const learningStore = db.objectStoreNames.contains(LEARNING_STORE) ? e.target.transaction.objectStore(LEARNING_STORE) : db.createObjectStore(LEARNING_STORE);
            if (!learningStore.indexNames.contains('timestamp')) learningStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      } catch (err) {
        db.close();
        reject(new Error("Error during DB upgrade: " + err.message));
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Error opening DB"));
  });
}

// --- UNIVERSAL SYNC CHANNEL (BroadcastChannel Fallback) ---
const SYNC_CHANNEL = "gacha_hosp_sync";
let syncChannel = null;

if (window.BroadcastChannel) {
    syncChannel = new BroadcastChannel(SYNC_CHANNEL);
} else {
    // Universal Fallback for older Safari/iOS [cite: 4, 11]
    syncChannel = {
        postMessage: (msg) => {
            localStorage.setItem('gacha_hosp_sync_fallback', JSON.stringify({ ...msg, _ts: Date.now() }));
        },
        onmessage: null
    };
    window.addEventListener('storage', (e) => {
        if (e.key === 'gacha_hosp_sync_fallback' && syncChannel.onmessage) {
            try {
                const data = JSON.parse(e.newValue);
                syncChannel.onmessage({ data });
            } catch(err) { /* ignore */ }
        }
    });
}

/**
 * Saves economy state with optimistic locking and quota validation.
 */
export async function saveEconomyState(state) {
  const db = await initDB();
  const toSave = { ...state, _version: Date.now() };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const putReq = store.put(toSave, "economy_state");

    putReq.onerror = (e) => {
      if (e.target.error.name === 'QuotaExceededError') {
        console.error("Storage Quota Exceeded. Notify user.");
        window.dispatchEvent(new CustomEvent('storage-quota-exceeded'));
      }
      reject(putReq.error);
    };

    tx.oncomplete = () => {
      db.close();
      syncChannel.postMessage({ type: 'ECONOMY_UPDATE', timestamp: Date.now() });
      resolve();
      // Optional: Trigger background cleanup silently
      cleanOldLogs().catch(err => console.warn("Background cleanup failed:", err));
    };

    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(new Error("Transaction Aborted")); };
  });
}

/**
 * Loads state with Recursive Default Merging to prevent schema drift errors.
 */
export async function loadEconomyState(defaultState) {
  const db = await initDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get("economy_state");
    
    req.onsuccess = () => {
      const loaded = req.result;
      const merged = mergeWithDefault(loaded, defaultState);
      resolve(merged);
    };

    req.onerror = () => resolve({ ...defaultState });
    tx.oncomplete = () => db.close();
  });
}

/**
 * Subscribes to cross-tab updates.
 */
export function onDatabaseUpdate(callback) {
    if (syncChannel) {
        syncChannel.onmessage = (event) => {
            if (event.data && event.data.type === 'ECONOMY_UPDATE') {
                callback();
            }
        };
    }
}

/**
 * Deep merge to ensure all expected keys from defaultState exist.
 */
function mergeWithDefault(loaded, defaultState) {
  if (!loaded) return { ...defaultState };
  const result = { ...defaultState, ...loaded };
  
  // Recursively handle known complex sub-objects to prevent partial data loss
  const nestedKeys = ['roster', 'caseHistory', 'discoveredPearls', 'rpgProfile'];
  nestedKeys.forEach(key => {
    if (loaded[key] && defaultState[key]) {
      result[key] = { ...defaultState[key], ...loaded[key] };
    } else if (loaded[key]) {
      result[key] = loaded[key];
    }
  });

  return result;
}

/**
 * Purgue records older than threshold to prevent device storage saturation.
 */
export async function cleanOldLogs(daysToKeep = 7) {
    const db = await initDB();
    const cutoff = Date.now() - (daysToKeep * 86400000);
    
    const storesToClean = [LOG_STORE, LEARNING_STORE];
    const results = [];

    for (const storeName of storesToClean) {
        results.push(new Promise((resolve) => {
            const tx = db.transaction(storeName, "readwrite");
            const store = tx.objectStore(storeName);
            const index = store.index('timestamp');
            const range = IDBKeyRange.upperBound(cutoff);
            
            const cursorReq = index.openCursor(range);
            cursorReq.onsuccess = (e) => {
                const cursor = e.target.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            cursorReq.onerror = () => resolve(); // Non-critical error
        }));
    }

    await Promise.all(results);
    db.close();
}

